import React, { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { submitDailyProgress, uploadPhoto, getDailyProgressReports } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Slider } from './ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Card, CardContent } from './ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { useDropzone } from 'react-dropzone';
import {
  Clock,
  Camera,
  MapPin,
  AlertTriangle,
  CloudRain,
  Users,
  FileText,
  Send,
  X,
  Upload,
  Loader2,
  CheckCircle,
  Shield,
  Briefcase,
  TrendingDown
} from 'lucide-react';
import { toast } from 'sonner';

// Threshold for drastic decrease warning (in percentage points)
const DRASTIC_DECREASE_THRESHOLD = 10;

const DailyProgressForm = ({ 
  open, 
  onOpenChange, 
  workOrder, 
  onSuccess,
  lastPercentComplete = null // Previous percent complete from last report
}) => {
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [showDecreaseWarning, setShowDecreaseWarning] = useState(false);
  const [previousPercent, setPreviousPercent] = useState(lastPercentComplete);
  const [form, setForm] = useState({
    project_status: 'active',
    work_completed_text: '',
    percent_complete: workOrder?.percent_complete || 0,
    hours_worked: '',
    delays_issues_text: '',
    material_needed_text: '',
    weather_impact: false,
    weather_impact_notes: '',
    safety_flag: false,
    safety_details: '',
    union_flag: false,
    union_details: ''
  });

  // Fetch the last report to get previous percent complete
  useEffect(() => {
    const fetchLastReport = async () => {
      if (workOrder?.id && lastPercentComplete === null) {
        try {
          const response = await getDailyProgressReports(workOrder.id, 1);
          if (response.data.reports && response.data.reports.length > 0) {
            setPreviousPercent(response.data.reports[0].percent_complete);
          } else {
            setPreviousPercent(workOrder?.percent_complete || 0);
          }
        } catch (error) {
          setPreviousPercent(workOrder?.percent_complete || 0);
        }
      }
    };
    if (open) {
      fetchLastReport();
    }
  }, [workOrder?.id, open, lastPercentComplete]);

  const currentTime = new Date().toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true 
  });
  
  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Photo upload with dropzone
  const onDrop = useCallback(async (acceptedFiles) => {
    const newPhotos = acceptedFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      uploading: false,
      uploaded: false,
      id: null
    }));
    setPhotos(prev => [...prev, ...newPhotos]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    multiple: true
  });

  const removePhoto = (index) => {
    setPhotos(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const uploadPhotos = async () => {
    if (photos.length === 0) return [];
    
    setUploadingPhotos(true);
    const uploadedIds = [];
    
    const today = new Date().toISOString().split('T')[0];
    
    for (let i = 0; i < photos.length; i++) {
      if (photos[i].uploaded) {
        uploadedIds.push(photos[i].id);
        continue;
      }
      
      try {
        const formData = new FormData();
        formData.append('file', photos[i].file);
        formData.append('work_order_id', workOrder.id);
        formData.append('tags', `Daily Progress - ${today}`);
        formData.append('description', `Daily Progress Photo - ${today}`);
        
        const response = await uploadPhoto(formData);
        uploadedIds.push(response.data.id);
        
        setPhotos(prev => {
          const updated = [...prev];
          updated[i] = { ...updated[i], uploaded: true, id: response.data.id };
          return updated;
        });
      } catch (error) {
        console.error('Photo upload failed:', error);
      }
    }
    
    setUploadingPhotos(false);
    return uploadedIds;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!form.work_completed_text.trim()) {
      toast.error('Please describe the work completed today');
      return;
    }
    if (!form.hours_worked || parseFloat(form.hours_worked) <= 0) {
      toast.error('Please enter valid hours worked');
      return;
    }
    
    // Check for drastic decrease in percent complete
    if (previousPercent !== null && previousPercent !== undefined) {
      const decrease = previousPercent - form.percent_complete;
      if (decrease >= DRASTIC_DECREASE_THRESHOLD) {
        setShowDecreaseWarning(true);
        return;
      }
    }
    
    await submitReport();
  };

  const submitReport = async () => {
    setSubmitting(true);
    
    try {
      // Upload photos first if any
      const photoIds = await uploadPhotos();
      
      // Submit the report
      const reportData = {
        work_order_id: workOrder.id,
        project_status: form.project_status,
        work_completed_text: form.work_completed_text.trim(),
        percent_complete: form.percent_complete,
        hours_worked: parseFloat(form.hours_worked),
        delays_issues_text: form.delays_issues_text.trim() || null,
        material_needed_text: form.material_needed_text.trim() || null,
        weather_impact: form.weather_impact,
        weather_impact_notes: form.weather_impact ? form.weather_impact_notes.trim() : null,
        safety_flag: form.safety_flag,
        safety_details: form.safety_flag ? form.safety_details.trim() : null,
        union_flag: form.union_flag,
        union_details: form.union_flag ? form.union_details.trim() : null
      };
      
      const response = await submitDailyProgress(workOrder.id, reportData);
      
      toast.success(
        response.data.status === 'on_time' 
          ? 'Daily progress report submitted on time!' 
          : 'Daily progress report submitted (late)'
      );
      
      // Reset form
      setForm({
        project_status: 'active',
        work_completed_text: '',
        percent_complete: form.percent_complete,
        hours_worked: '',
        delays_issues_text: '',
        material_needed_text: '',
        weather_impact: false,
        weather_impact_notes: '',
        safety_flag: false,
        safety_details: '',
        union_flag: false,
        union_details: ''
      });
      setPhotos([]);
      
      onSuccess?.(response.data);
      onOpenChange(false);
    } catch (error) {
      console.error('Submit failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit daily progress');
    } finally {
      setSubmitting(false);
    }
  };

  if (!workOrder) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[95vh] overflow-y-auto p-0 sm:p-6 bg-slate-800 border-slate-600 text-white">
        {/* Mobile-optimized header */}
        <DialogHeader className="sticky top-0 bg-slate-700 z-10 p-4 sm:p-0 border-b border-slate-600 sm:border-0 sm:bg-transparent">
          <DialogTitle className="flex items-center gap-2 text-lg text-white">
            <FileText className="w-5 h-5 text-blue-400" />
            Daily Progress Report
          </DialogTitle>
          <div className="text-sm text-slate-300 mt-1">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              {workOrder.wo_number} - {workOrder.location_name}
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 p-4 sm:p-0">
          {/* Auto-filled Info Card */}
          <Card className="bg-slate-700 border-slate-500">
            <CardContent className="p-3 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-300">Date:</span>
                <span className="font-medium text-white">{currentDate}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-300 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Time:
                </span>
                <span className="font-medium text-blue-400">{currentTime}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-300">Submitted by:</span>
                <span className="font-medium text-white">{user?.full_name}</span>
              </div>
            </CardContent>
          </Card>

          {/* Project Status */}
          <div>
            <Label className="text-sm font-medium text-white">Project Status</Label>
            <Select 
              value={form.project_status} 
              onValueChange={(v) => setForm(prev => ({ ...prev, project_status: v }))}
            >
              <SelectTrigger className="mt-1 bg-slate-700 border-slate-500 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Work Completed - Required */}
          <div>
            <Label className="text-sm font-medium text-white">
              Work Completed Today <span className="text-red-400">*</span>
            </Label>
            <Textarea
              value={form.work_completed_text}
              onChange={(e) => setForm(prev => ({ ...prev, work_completed_text: e.target.value }))}
              placeholder="Describe work completed so far (what bays, what equipment, what steps)..."
              className="mt-1 min-h-[100px] bg-slate-700 border-slate-500 text-white placeholder:text-slate-400"
              required
            />
          </div>

          {/* Percent Complete - Slider */}
          <div>
            <Label className="text-sm font-medium text-white flex items-center justify-between">
              <span>Percent Complete <span className="text-red-400">*</span></span>
              <span className="text-green-400 font-bold text-lg">{form.percent_complete}%</span>
            </Label>
            <Slider
              value={[form.percent_complete]}
              onValueChange={([v]) => setForm(prev => ({ ...prev, percent_complete: v }))}
              max={100}
              step={5}
              className="mt-3"
            />
            <div className="flex justify-between text-xs text-slate-400 mt-1">
              <span>0%</span>
              <span>50%</span>
              <span>100%</span>
            </div>
          </div>

          {/* Hours Worked - Required */}
          <div>
            <Label className="text-sm font-medium text-white">
              Hours Worked Today (Crew Total) <span className="text-red-400">*</span>
            </Label>
            <Input
              type="number"
              min="0"
              step="0.5"
              value={form.hours_worked}
              onChange={(e) => setForm(prev => ({ ...prev, hours_worked: e.target.value }))}
              placeholder="e.g., 8.5"
              className="mt-1 bg-slate-700 border-slate-500 text-white placeholder:text-slate-400"
              required
            />
          </div>

          {/* Delays/Issues - Optional */}
          <div>
            <Label className="text-sm font-medium text-amber-300 flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" />
              Delays / Issues (Optional)
            </Label>
            <Textarea
              value={form.delays_issues_text}
              onChange={(e) => setForm(prev => ({ ...prev, delays_issues_text: e.target.value }))}
              placeholder="Any delays, hold-offs, waiting on clearances, material, or other issues?"
              className="mt-1 min-h-[80px] bg-slate-700 border-slate-500 text-white placeholder:text-slate-400"
            />
          </div>

          {/* Material Needed - Optional */}
          <div>
            <Label className="text-sm font-medium text-white">Material/Equipment Needed (Optional)</Label>
            <Textarea
              value={form.material_needed_text}
              onChange={(e) => setForm(prev => ({ ...prev, material_needed_text: e.target.value }))}
              placeholder="Anything that might hold up progress tomorrow if not available?"
              className="mt-1 min-h-[60px] bg-slate-700 border-slate-500 text-white placeholder:text-slate-400"
            />
          </div>

          {/* Weather Impact Toggle */}
          <Card className="border-blue-600 bg-slate-700">
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium flex items-center gap-2 cursor-pointer text-white">
                  <CloudRain className="w-4 h-4 text-blue-400" />
                  Weather Impact?
                </Label>
                <Switch
                  checked={form.weather_impact}
                  onCheckedChange={(v) => setForm(prev => ({ ...prev, weather_impact: v }))}
                />
              </div>
              {form.weather_impact && (
                <Textarea
                  value={form.weather_impact_notes}
                  onChange={(e) => setForm(prev => ({ ...prev, weather_impact_notes: e.target.value }))}
                  placeholder="Describe weather impact..."
                  className="mt-3 min-h-[60px] bg-slate-600 border-slate-500 text-white placeholder:text-slate-400"
                />
              )}
            </CardContent>
          </Card>

          {/* Photo Upload */}
          <div>
            <Label className="text-sm font-medium text-white flex items-center gap-2 mb-2">
              <Camera className="w-4 h-4" />
              Add Photos from Today (Optional)
            </Label>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                isDragActive ? 'border-blue-500 bg-blue-900/30' : 'border-slate-500 hover:border-blue-400 bg-slate-700'
              }`}
            >
              <input {...getInputProps()} />
              <Upload className="w-8 h-8 mx-auto mb-2 text-slate-500" />
              <p className="text-sm text-slate-400">
                {isDragActive ? 'Drop photos here' : 'Tap to add photos or drag & drop'}
              </p>
            </div>
            
            {photos.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {photos.map((photo, index) => (
                  <div key={index} className="relative w-16 h-16">
                    <img
                      src={photo.preview}
                      alt={`Upload ${index + 1}`}
                      className="w-full h-full object-cover rounded-lg"
                    />
                    {photo.uploaded && (
                      <CheckCircle className="absolute -top-1 -right-1 w-4 h-4 text-green-500 bg-slate-900 rounded-full" />
                    )}
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Safety & Observations Section */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-white flex items-center gap-2">
              <Shield className="w-4 h-4 text-red-400" />
              Safety & Observations
            </h4>
            
            {/* Safety Incident Toggle */}
            <Card className="border-red-600 bg-slate-700">
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium flex items-center gap-2 cursor-pointer text-white">
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                    Any safety incidents, near misses, or concerns?
                  </Label>
                  <Switch
                    checked={form.safety_flag}
                    onCheckedChange={(v) => setForm(prev => ({ ...prev, safety_flag: v }))}
                  />
                </div>
                {form.safety_flag && (
                  <Textarea
                    value={form.safety_details}
                    onChange={(e) => setForm(prev => ({ ...prev, safety_details: e.target.value }))}
                    placeholder="Describe safety-related event or concern..."
                    className="mt-3 min-h-[80px] bg-slate-600 border-slate-500 text-white placeholder:text-slate-400"
                  />
                )}
              </CardContent>
            </Card>

            {/* Union/Overtime Toggle */}
            <Card className="border-amber-600 bg-slate-700">
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium flex items-center gap-2 cursor-pointer text-white">
                    <Briefcase className="w-4 h-4 text-amber-400" />
                    Any union/overtime concerns?
                  </Label>
                  <Switch
                    checked={form.union_flag}
                    onCheckedChange={(v) => setForm(prev => ({ ...prev, union_flag: v }))}
                  />
                </div>
                {form.union_flag && (
                  <Textarea
                    value={form.union_details}
                    onChange={(e) => setForm(prev => ({ ...prev, union_details: e.target.value }))}
                    placeholder="Describe overtime or union-related concern..."
                    className="mt-3 min-h-[80px] bg-slate-600 border-slate-500 text-white placeholder:text-slate-400"
                  />
                )}
              </CardContent>
            </Card>
          </div>

          {/* Submit Button - Sticky on mobile */}
          <div className="sticky bottom-0 bg-slate-800 pt-4 pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 border-t border-slate-600 sm:border-0">
            {/* Warning if percent is lower */}
            {previousPercent !== null && form.percent_complete < previousPercent && (
              <div className="mb-3 p-2 bg-amber-900/50 border border-amber-600 rounded-lg flex items-center gap-2 text-amber-200 text-sm">
                <TrendingDown className="w-4 h-4" />
                <span>
                  Progress decreased from {previousPercent}% to {form.percent_complete}%
                </span>
              </div>
            )}
            <Button
              type="submit"
              disabled={submitting || uploadingPhotos}
              className="w-full h-12 text-base bg-blue-600 hover:bg-blue-700"
            >
              {submitting || uploadingPhotos ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {uploadingPhotos ? 'Uploading Photos...' : 'Submitting...'}
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Submit Progress Report
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>

      {/* Drastic Decrease Warning Dialog */}
      <AlertDialog open={showDecreaseWarning} onOpenChange={setShowDecreaseWarning}>
        <AlertDialogContent className="bg-slate-900 border-slate-700 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-400">
              <TrendingDown className="w-5 h-5" />
              Significant Progress Decrease
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300">
              You are reporting <span className="font-bold text-white">{form.percent_complete}%</span> complete, 
              which is <span className="font-bold text-red-400">{previousPercent - form.percent_complete}% lower</span> than 
              the previous report (<span className="font-bold text-white">{previousPercent}%</span>).
              <br /><br />
              Are you sure this is correct? This may require explanation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 border-slate-600 text-white hover:bg-slate-600">
              Go Back & Adjust
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                setShowDecreaseWarning(false);
                submitReport();
              }}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              Yes, Submit Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};

export default DailyProgressForm;
